// 轻量国际化：中文原文即 key，EN 词典映射英文；缺失条目回退中文（渐进覆盖，无第三方依赖）
// - t(text, vars)：静态界面文案，{x} 占位符插值
// - ts(text)：服务端返回的动态文案（错误提示/站内消息/决策理由等），按模板正则翻译，
//   后端源码保持中文不动，历史入库内容也能翻
import { createContext, useContext, useCallback } from 'react';

// 静态文案词典：中文 → 英文
const EN = {
  // 通用
  '刷新': 'Refresh', '保存': 'Save', '删除': 'Delete', '取消': 'Cancel', '返回': 'Back',
  '导出 Excel': 'Export Excel', '时间': 'Time', '方向': 'Side', '数量': 'Amount', '金额': 'Amount',
  '地址': 'Address', '名称': 'Name', '操作': 'Actions', '交易': 'Tx', '来源': 'Source',
  '手动': 'Manual', '自动': 'Auto', '内部': 'Internal', '外部': 'External', '身份': 'Type',
  '全部': 'All', '买入': 'Buy', '卖出': 'Sell', '转入': 'In', '转出': 'Out', '加载中…': 'Loading…',
  '未查询': 'N/A', '暂无记录': 'No records', '暂无消息': 'No messages', '标签': 'Tags',

  // App
  '钱包': 'Wallets', '交易记录': 'Transactions', '设置': 'Settings',
  '加载钱包失败: ': 'Failed to load wallets: ',

  // 钱包列表
  '钱包（{n}）': 'Wallets ({n})', '分发 gas': 'Distribute gas', '刷新余额': 'Refresh balances',
  'USDT 余额': 'USDT Balance', 'USGNAA 余额': 'USGNAA Balance', 'BNB 余额（gas）': 'BNB (gas)',
  '查看交易': 'View txs', '转账': 'Send', '私钥未导入': 'Private key not imported',
  'BNB 不足以支付 gas': 'Insufficient BNB for gas', '添加标签': 'Add tags',
  '将从 BNB 最多的钱包，给 BNB 不足 0.0005 的其他钱包各转 0.0005 BNB（逐笔链上转账）。确认执行？':
    'The wallet with the most BNB will send 0.0005 BNB to each wallet below 0.0005 (on-chain transfers). Proceed?',
  '确认分发': 'Distribute', '所有钱包 gas 均已充足，无需分发': 'All wallets have sufficient gas',
  '分发失败: ': 'Distribution failed: ', '转账失败: ': 'Transfer failed: ',
  '我的钱包': 'My wallets', '地址簿': 'Address book', '币种': 'Token',
  '可用余额：': 'Available: ', '选择收款地址': 'Select recipient',
  '收款方（限钱包列表与地址簿，防误转）': 'Recipient (wallets & address book only, anti-mistransfer)',
  '确认转账': 'Confirm', '全部余额': 'Max',
  '转账将直接上链执行；如需转给列表外的地址，请先在设置 → 地址簿中添加':
    'Transfers execute on-chain directly. To send to other addresses, add them in Settings → Address book first',
  '保存失败: ': 'Save failed: ', '更新失败: ': 'Update failed: ', '删除失败: ': 'Delete failed: ',
  '标签 · {label}': 'Tags · {label}',
  '选择已有标签，或直接输入新标签后回车': 'Pick existing tags or type new ones and press Enter',
  '标签用于给钱包分组标记（如：主力、测试、备用）；单个标签最长 32 字符，每个钱包最多 20 个':
    'Tags group wallets (e.g. main, test, reserve); max 32 chars each, up to 20 per wallet',
  '已保存 {label} 的标签': 'Tags saved for {label}',
  '已从 {source} 给 {n} 个钱包各分发 gas': 'Distributed gas from {source} to {n} wallet(s)',
  '转账成功：{amount} {token}（{tx}...）': 'Sent {amount} {token} ({tx}...)',
  '转账 · {label}': 'Send · {label}', '全部_max': 'Max', '最多 {max}': 'Max {max}',

  // 行情 / Swap
  '行情': 'Market', '池内现价': 'Pool price',
  '池内现价：基于池子储备比值实时计算（getReserves），不含手续费与滑点，每分钟刷新':
    'Pool spot price: computed from pool reserve ratio (getReserves), excl. fees & slippage, refreshes every minute',
  '24小时': '24h', '7天': '7d', '30天': '30d', '90天': '90d',
  '更新于 {time}（每分钟自动刷新）': 'Updated {time} (auto-refresh every minute)',
  '开': 'O', '高': 'H', '低': 'L', '收': 'C', '量': 'V',
  '确认 Swap': 'Confirm Swap', '执行中…': 'Executing…', '确认执行': 'Execute',
  '钱包（gas 不足的已禁用）': 'Wallet (gas-insufficient disabled)',
  '金额（USDT）': 'Amount (USDT)', '金额（USGNAA）': 'Amount (USGNAA)',
  'USDT → USGNAA（买入）': 'USDT → USGNAA (Buy)', 'USGNAA → USDT（卖出）': 'USGNAA → USDT (Sell)',
  '预计换得': 'Est. received', '最少到账': 'Min. received', '汇率': 'Rate', '价格影响': 'Price impact',
  '交易手续费': 'Trading fee', '路径': 'Route', '滑点保护': 'Slippage', '池子储备': 'Pool reserves',
  '自动补 gas': 'auto gas top-up', 'Swap 成功': 'Swap succeeded', 'Swap 失败': 'Swap failed',
  '报价获取失败: ': 'Quote failed: ', '正在获取最新报价…': 'Fetching latest quote…',
  '没有可用的钱包（余额或 gas 不足）': 'No eligible wallets (insufficient balance or gas)',

  // 交易记录
  '本地记录': 'Local Records', '链上记录': 'On-chain Records', '钱包转账': 'Wallet Transfers',
  'PancakeSwap 成交': 'PancakeSwap Trades', '支出': 'Spent', '换得': 'Received', '状态': 'Status',
  '进行中': 'Pending', '成功': 'Success', '失败': 'Failed', '从 → 到': 'From → To',
  '实时抓取': 'Live', '本地存档': 'Archive', '同步最新': 'Sync latest',
  '全量同步（翻页抓取全部历史，较慢）': 'Full sync (all pages, slower)',
  '选择钱包查询链上记录': 'Select a wallet', '内部地址（地址簿）': 'Internal (address book)',
  '在 BscScan 查看该地址全部记录': 'View all records on BscScan',
  '记录实时抓取自 BscTrace（最新在前）；"同步最新"只抓最新一页入库，下拉可选全量同步':
    'Live data from BscTrace (newest first); "Sync latest" stores the newest page, use dropdown for full sync',
  '本地存档：历次同步累积的链上记录（已去重，最新在前）':
    'Archive: on-chain records accumulated by syncs (deduplicated, newest first)',
  '该地址近期无相关链上记录': 'No recent on-chain records', '请先选择钱包': 'Select a wallet first',
  '同步失败: ': 'Sync failed: ',

  // PancakeSwap 成交
  '同步成交': 'Sync trades', '全量回溯（BscTrace 发现历史交易 + 链上收据解析，较慢）':
    'Full backfill (discover via BscTrace + on-chain receipts, slower)',
  '库内共 {n} 条成交': '{n} trades stored',
  '成交解析自 USDT/USGNAA 交易对合约的链上 Swap 事件（含外部地址参与的交易）；「同步成交」增量抓新区块，下拉「全量回溯」可补齐历史；已随 swap 操作与每小时定时任务自动入库，统计基于本地存档':
    'Trades parsed from on-chain Swap events of the USDT/USGNAA pair (incl. external addresses); "Sync trades" fetches new blocks incrementally, dropdown backfills history; auto-synced after swaps and hourly, stats based on local archive',
  '总成交笔数': 'Total trades', '参与地址': 'Participants', 'USDT 总成交量': 'USDT volume',
  'USGNAA 总成交量': 'USGNAA volume', '买{b} / 卖{s}': 'Buy {b} / Sell {s}',
  '内部{i} / 外部{e}': 'Int {i} / Ext {e}',
  '参与方统计（按 USDT 成交量倒序）': 'Participants (by USDT volume desc)',
  '参与方': 'Participant', '成交笔数': 'Trades', '买入 USGNAA': 'Bought USGNAA',
  '卖出 USGNAA': 'Sold USGNAA', 'USDT 成交量': 'USDT Volume', '当前 USDT': 'USDT Now',
  '当前 USGNAA': 'USGNAA Now', '暂无成交，请先同步': 'No trades, sync first',
  '成交明细（全部 {n} 条）': 'Trade details (all {n})',
  '成交明细（筛出 {f} / {n} 条）': 'Trade details ({f} of {n})',
  '开始日期': 'Start date', '结束日期': 'End date', '交易者（全部）': 'Trader (all)',
  '买入 USGNAA_tag': 'Buy USGNAA', '卖出 USGNAA_tag': 'Sell USGNAA',
  '单价 (USDT)': 'Price (USDT)', '交易者': 'Trader', '区块': 'Block',
  '内部参与钱包当前持有：USDT {u} · USGNAA {g}（{n} 个钱包，成交 {v} USDT）':
    'Internal participants hold: USDT {u} · USGNAA {g} ({n} wallets, {v} USDT traded)',
  '外部参与地址当前持有：USDT {u} · USGNAA {g}（已查询 {q}/{n} 个地址，成交 {v} USDT）':
    'External participants hold: USDT {u} · USGNAA {g} ({q}/{n} addresses queried, {v} USDT traded)',

  // 设置
  '自动交易': 'Auto Trading',
  '为常用地址设置名称后，交易记录等处会以名称标签展示该地址；标记为「内部」的地址在 PancakeSwap 成交统计中与 10 个固定钱包一起归入内部组':
    'Named addresses show as labels across the app; addresses marked "Internal" are grouped with the 10 wallets in trade stats',
  '0x 开头的 42 位地址': '42-char address starting with 0x',
  '名称（如：币安提币、老板钱包）': 'Name (e.g. Binance, boss wallet)',
  '已保存（同地址重复保存会更新名称与身份）': 'Saved (re-saving the same address updates name & type)',
  '「{name}」已标记为{type}': '"{name}" marked as {type}',
  '删除「{name}」？': 'Delete "{name}"?', '已删除': 'Deleted', '已清空': 'Cleared',
  '请输入合法的 0x 开头 42 位地址': 'Enter a valid 42-char address starting with 0x',
  '请输入名称': 'Enter a name', '内': 'Int', '外': 'Ext',

  // 自动交易面板
  '自动交易运行中：调度器每分钟评估一次，按价格区间与小时目标自动执行 USDT ⇄ USGNAA':
    'Auto trading is ON: evaluates every minute, executes USDT ⇄ USGNAA per price range & hourly targets',
  '自动交易已关闭：保存配置并打开开关后生效':
    'Auto trading is OFF: save config and turn on the switch to start',
  '价格高于区间上限时卖出 USGNAA 压价，低于下限时买入拉价，区间内小额随机双向交易维持活跃度；连续 3 次执行失败会自动停机并写入站内消息':
    'Sells USGNAA above the upper bound, buys below the lower bound, random small trades within range; auto-stops after 3 consecutive failures with a notification',
  '本小时笔数': 'Trades this hour', '本小时成交量（USDT）': 'Volume this hour (USDT)',
  '自动化开关': 'Enabled', '开_ON': 'ON', '关_OFF': 'OFF',
  '价格维持区间': 'Price range', '1 USGNAA = ? USDT；越界时定向买卖拉回区间':
    '1 USGNAA = ? USDT; directional trades pull price back when out of range',
  '下限': 'Min', '上限': 'Max', '每小时目标': 'Hourly target',
  '成交量按 USDT 折算；按自然小时重置，下单时刻随机散布在整点内（间隔不规律，临近整点自动补齐目标）':
    'Volume in USDT; resets each hour, order times randomly scattered within the hour (auto catch-up near the end)',
  '笔': 'trades', '单笔金额区间': 'Per-trade amount',
  'USDT 计价，区间内随机；卖出方向自动换算成 USGNAA 数量':
    'In USDT, random within range; sell side converted to USGNAA automatically',
  '金额形式': 'Amount style', '小数': 'Decimal', '整数': 'Integer',
  '整数：下单金额取整（如 12 USDT）；小数：保留 2 位（如 12.37 USDT）':
    'Integer: whole amounts (e.g. 12 USDT); Decimal: 2 decimal places (e.g. 12.37 USDT)',
  '交易钱包标签': 'Trading wallet tags', '命中任一标签的钱包参与自动交易；留空 = 全部钱包':
    'Wallets with any of these tags trade; empty = all wallets',
  '留空表示全部钱包': 'Empty = all wallets', '资金调度标签': 'Funding wallet tags',
  '交易钱包余额不够时，从命中这些标签的钱包自动转入 USDT/USGNAA；留空 = 不自动互转':
    'When trading wallets lack balance, funds are pulled from wallets with these tags; empty = no auto transfers',
  '留空表示不自动互转': 'Empty = no auto transfers', '刷新标签候选': 'Refresh tag options',
  '保存配置': 'Save config', '试运行（不下单）': 'Dry run (no orders)', '刷新状态': 'Refresh status',
  '已保存，自动交易运行中（每分钟评估一次）': 'Saved. Auto trading is running (evaluates every minute)',
  '已保存，自动交易处于关闭状态': 'Saved. Auto trading is off',
  '试运行失败: ': 'Dry run failed: ', '标签刷新失败: ': 'Tag refresh failed: ',
  '（{tag}（{n} 个钱包））': '{tag} ({n} wallets)',

  // 消息
  '消息': 'Messages', '站内消息': 'Notifications', '全部已读': 'Mark all read', '清空': 'Clear all',
  '清空全部消息？': 'Clear all messages?', '未读': 'Unread', '信息': 'Info', '警告': 'Warning',
  '异常': 'Error', '来源：': 'Source: ', '自动交易_src': 'auto trading',
  '加载消息失败: ': 'Failed to load messages: ', '操作失败: ': 'Operation failed: ',
  '清空失败: ': 'Clear failed: ',

  // 交易记录 / PancakeSwap 成交（补充）
  '钱包_col': 'Wallet',
  '增量同步完成：抓取 {fetched} 条，新增 {inserted} 条，本地共 {total} 条':
    'Incremental sync done: fetched {fetched}, added {inserted}, {total} total locally',
  '全量同步完成：抓取 {fetched} 条，新增 {inserted} 条，本地共 {total} 条':
    'Full sync done: fetched {fetched}, added {inserted}, {total} total locally',
  '全量回溯完成：解析 {scannedTx} 笔新交易，入库成交 {fetched} 条，新增 {inserted} 条，库内共 {total} 条':
    'Full backfill done: parsed {scannedTx} new txs, stored {fetched} trades, added {inserted}, {total} total',
  '增量同步完成：抓取 {fetched} 条，新增 {inserted} 条，库内共 {total} 条':
    'Incremental sync done: fetched {fetched}, added {inserted}, {total} total',
  '（{c} 笔）': ' ({c} trades)',
  '（买{b}/卖{s}）': '(Buy {b}/Sell {s})',

  // Swap 弹窗 / 报价（补充）
  '钱包_单': 'Wallet', '在 BscScan 查看': 'view on BscScan', '支出金额': 'Spend amount',
  '选择钱包': 'Select wallet', '选择金额档位': 'Select amount', '报价加载中...': 'Loading quote…',
  '选择钱包与金额后展示报价': 'Pick a wallet and amount to see the quote',
  '最少到账（滑点 {pct}%）': 'Min received (slippage {pct}%)', '执行汇率': 'Exec. rate',
  '池子流动性': 'Pool liquidity', '交易手续费（0.25%）': 'Trading fee (0.25%)', '未知': 'N/A',
  '·自动补gas': ' ·auto gas', 'gas不足需分发': 'gas low, distribute',
  '补gas所需USDT不足': 'insufficient USDT for gas', '{sym}不足': 'insufficient {sym}',
  '当前没有可执行的钱包：需要私钥已导入、{sym} 足额且 BNB ≥ {min}。可先点钱包列表的"分发 gas"，或外部充值 BNB':
    'No executable wallet: needs imported key, enough {sym}, and BNB ≥ {min}. Use "Distribute gas" or deposit BNB first',
  '该钱包 BNB 低于 {min}，执行前会自动先用 {usdt} USDT 换 BNB 补 gas':
    'This wallet has BNB below {min}; {usdt} USDT will be swapped for BNB before executing',
  '余额 {bal} {sym} · 确认执行时将按最新链上报价重新计算滑点保护下限，实际到账以链上成交为准':
    'Balance {bal} {sym} · slippage floor recomputed from the latest quote at execution; actual amount per on-chain settlement',

  // K 线读数（区别于自动交易开关的「开」）
  '开_k': 'O', '高_k': 'H', '低_k': 'L', '收_k': 'C', '量_k': 'V',

  // 自动交易试运行预览
  '将用 {label} 以 {amount} {sym} 下单（≈{v} USDT）': 'Will trade via {label}: {amount} {sym} (≈{v} USDT)',
  '将先调度资金：{from} → {to} 转 {amount} {token}': 'Will move funds first: {from} → {to} sends {amount} {token}',
  '本次将跳过：{reason}': 'Will skip: {reason}',
  '当前价格 {price} USDT/USGNAA · 本小时已 {trades} 笔 / {vol} USDT':
    'Current price {price} USDT/USGNAA · this hour: {trades} trades / {vol} USDT',
  '依据：': 'Reason: ',

  // 价格提醒
  '价格提醒': 'Price Alert',
  '已保存，价格提醒已开启': 'Saved. Price alert is on',
  '已保存，价格提醒已关闭': 'Saved. Price alert is off',
  '测试消息已发送，请检查接收端': 'Test message sent, check the receiver',
  '测试发送失败: ': 'Test send failed: ',
  '价格提醒已开启：每分钟检查池内价，越界时发送 webhook':
    'Price alert is ON: checks the pool price every minute and sends a webhook when out of range',
  '价格提醒已关闭：填好配置并打开开关后生效':
    'Price alert is OFF: fill in the config and turn on the switch to enable',
  '池内价高于上限或低于下限时，向配置的接口 POST 一条英文告警；同方向持续越界按冷却时长去重，回到区间内后再次越界会重新提醒':
    'When the pool price goes above the upper bound or below the lower bound, an English alert is POSTed to the configured endpoint; repeats in the same direction are throttled by the cooldown, and a return into range re-arms the alert',
  '提醒开关': 'Enabled', '提醒价格区间': 'Alert range',
  '1 USGNAA = ? USDT；超出区间即触发提醒': '1 USGNAA = ? USDT; alerts trigger when out of range',
  '接口地址': 'Endpoint URL',
  '接收 POST 的 webhook 地址，如 http://localhost:8000/api/v1/send':
    'Webhook URL that receives the POST, e.g. http://localhost:8000/api/v1/send',
  '接口 Key': 'API Key',
  '作为 X-Api-Key 请求头发送；留空表示保留已存 Key 不修改':
    'Sent as the X-Api-Key header; leave empty to keep the stored key',
  '已设置（留空则不修改）': 'Set (leave empty to keep)', '填写接口 Key': 'Enter the API key',
  '接收方': 'Recipients',
  'recipients 数组，如 group.xxx；输入后回车添加，可多个':
    'recipients array, e.g. group.xxx; press Enter to add, multiple allowed',
  '输入接收方标识后回车': 'Type a recipient and press Enter',
  '重复提醒冷却': 'Repeat cooldown',
  '同方向持续越界时，间隔多久才再次提醒（分钟）':
    'How long before re-alerting while still out of range in the same direction (minutes)',
  '分钟': 'min', '发送测试消息': 'Send test message', '价格提醒_src': 'price alert',

  // 登录
  '请登录以继续': 'Please sign in to continue', '用户名': 'Username', '密码': 'Password',
  '请输入用户名': 'Enter your username', '请输入密码': 'Enter your password', '登录': 'Sign in',
  '确认登出？': 'Sign out?', '登出': 'Sign out'
};

// 服务端动态文案：正则模板 → 英文（顺序匹配，命中多个则逐个替换）
const SERVER_PATTERNS = [
  // 登录鉴权
  [/^用户名或密码错误$/, 'Incorrect username or password'],
  [/^请输入用户名与密码$/, 'Please enter username and password'],
  [/^未登录或登录已过期$/, 'Not signed in or session expired'],
  [/^登录尝试过于频繁，请稍后再试$/, 'Too many login attempts, please try again later'],
  [/^当前密码错误$/, 'Current password is incorrect'],
  [/^新密码至少 (\d+) 位$/, 'New password must be at least $1 characters'],
  // 价格提醒站内消息（标题 + 括注错误）；正文 message 本身已是英文
  [/^价格越界提醒已发送$/, 'Price alert sent'],
  [/^价格越界提醒发送失败$/, 'Price alert failed to send'],
  [/^请先填写接口地址与接收方并保存$/, 'Fill in the endpoint URL and recipients, then save'],
  [/^测试发送失败: (.*)$/, 'Test send failed: $1'],
  [/^启用提醒时必须填写接口地址与至少一个接收方$/, 'When enabled, an endpoint URL and at least one recipient are required'],
  [/^价格下限不能大于上限$/, 'Price lower bound cannot exceed the upper bound'],
  [/^冷却时长需在 0~(\d+) 分钟之间$/, 'Cooldown must be between 0 and $1 minutes'],
  [/^接口地址需以 http:\/\/ 或 https:\/\/ 开头$/, 'Endpoint URL must start with http:// or https://'],
  [/^接口地址过长$/, 'Endpoint URL too long'],
  [/^接收方最多 (\d+) 个$/, 'At most $1 recipients'],
  [/^钱包 (.+?) 的 (\S+) 余额不足（([\d.]+)）$/, 'Wallet $1 has insufficient $2 balance ($3)'],
  [/^钱包 (.+?) 的 USDT 余额不足 (.+)$/, 'Wallet $1 has insufficient USDT (needs $2)'],
  [/^钱包 (.+?) 的 USGNAA 余额不足 (.+)$/, 'Wallet $1 has insufficient USGNAA (needs $2)'],
  [/^钱包 (.+?) 有进行中的交易，请稍后再试$/, 'Wallet $1 has a pending transaction, try again later'],
  [/^钱包 (.+?) 的 BNB 不足以支付转账 gas，请先分发 gas$/, 'Wallet $1 lacks BNB for gas, distribute gas first'],
  [/^钱包 (.+?) 的私钥未导入.*$/, 'Private key of wallet $1 not imported'],
  [/^钱包 (.+?) 需自动补 gas 但 USDT 余额不足 (.+)$/, 'Wallet $1 needs gas top-up but lacks $2 USDT'],
  [/^钱包 (.+?) 的 BNB 过低（< ([\d.]+)），连自动补 gas 都无法执行，请先用"分发 gas"或外部充值$/,
    'Wallet $1 BNB too low (< $2) even for auto top-up; distribute gas or deposit externally'],
  [/^swap 执行失败: (.*)$/, 'Swap failed: $1'],
  [/^源钱包 (.+?) 的 BNB 不足：需要约 ([\d.]+)（(.+?)），当前 ([\d.]+)$/,
    'Source wallet $1 lacks BNB: needs ~$2, has $4'],
  [/^自动交易未启用$/, 'Auto trading is disabled'],
  [/^无法获取有效价格$/, 'Cannot fetch a valid price'],
  [/^本小时笔数已达标（(\d+)\/(\d+)）$/, 'Hourly trade count reached ($1/$2)'],
  [/^本小时成交量已达标（(.+?)\/(.+?) USDT）$/, 'Hourly volume reached ($1/$2 USDT)'],
  [/^随机节奏等待（本小时还差 (\d+) 笔 \/ 剩 (\d+) 分钟，本分钟命中概率 (\d+)%）$/,
    'Random pacing: waiting ($1 trades left / $2 min remaining, $3% chance this minute)'],
  [/^价格 ([\d.]+) 高于上限 ([\d.]+)，卖出 USGNAA 压价$/, 'Price $1 above upper bound $2, selling USGNAA'],
  [/^价格 ([\d.]+) 低于下限 ([\d.]+)，买入 USGNAA 拉价$/, 'Price $1 below lower bound $2, buying USGNAA'],
  [/^价格 ([\d.]+) 在区间内，随机买入维持活跃$/, 'Price $1 within range, random buy for activity'],
  [/^价格 ([\d.]+) 在区间内，随机卖出维持活跃$/, 'Price $1 within range, random sell for activity'],
  [/^本小时剩余额度 ([\d.]+) USDT 不足单笔下限 (.+)$/, 'Remaining quota $1 USDT below per-trade minimum $2'],
  [/^没有匹配交易标签的钱包，请在钱包列表打标签或调整配置$/, 'No wallets match the trading tags; tag wallets or adjust config'],
  [/^匹配标签的钱包均不可用（私钥未导入或 BNB 不足）$/, 'No tagged wallet is operable (key not imported or BNB too low)'],
  [/^没有钱包持有足够的 (\S+)（需 (.+?)），且无法从互转钱包调度$/,
    'No wallet holds enough $1 (needs $2) and no funding wallet can cover it'],
  [/^(.+?) 的 (\S+) 不足（缺 (.+?)），先从 (.+?) 调度$/, '$1 lacks $2 (short $3), funding from $4 first'],
  [/^自动交易执行失败（连续 (\d+) 次）$/, 'Auto trade execution failed ($1 consecutive)'],
  [/^自动交易已自动停机$/, 'Auto trading auto-stopped'],
  [/^连续 (\d+) 次执行失败，已关闭自动交易开关，请排查后手动开启$/,
    'Stopped after $1 consecutive failures; investigate and re-enable manually'],
  [/^自动交易资金调度$/, 'Auto trading fund transfer'],
  [/^自动交易已跳过本次执行$/, 'Auto trading skipped this run'],
  [/^(.+?) → (.+?) 转账 (.+?) (\S+)（(.+?)）$/, '$1 → $2 transferred $3 $4 ($5)'],
  [/^金额不在允许档位内$/, 'Amount not in allowed presets'],
  [/^swap 方向非法$/, 'Invalid swap direction'],
  [/^未知的钱包地址$/, 'Unknown wallet address'],
  [/^未知的发送方钱包$/, 'Unknown sender wallet'],
  [/^收款地址不在钱包列表或地址簿中（防误转保护，可先在设置里加入地址簿）$/,
    'Recipient not in wallets or address book (anti-mistransfer; add it in Settings first)'],
  [/^收款地址格式非法$/, 'Invalid recipient address'],
  [/^不能转给自己$/, 'Cannot send to self'],
  [/^金额非法$/, 'Invalid amount'],
  [/^token 只支持 USDT 或 USGNAA$/, 'Only USDT or USGNAA supported'],
  [/^服务器内部错误$/, 'Internal server error'],
  [/^接口不存在$/, 'API not found'],
  [/^行情数据暂不可用：接口不可达且该粒度暂无本地存档，稍后重试即可$/,
    'Market data unavailable: API unreachable and no local archive yet, retry later'],
  [/^池内现价暂不可用$/, 'Pool price unavailable'],
  [/^地址格式非法$/, 'Invalid address'],
  [/^名称必须为 1~64 个字符$/, 'Name must be 1-64 characters'],
  [/^每个钱包最多 (\d+) 个标签$/, 'Up to $1 tags per wallet'],
  [/^标签长度不能超过 (\d+) 个字符$/, 'Tags must be at most $1 characters'],
  [/^价格区间下限不能大于上限$/, 'Price range min cannot exceed max'],
  [/^单笔下限不能大于上限$/, 'Per-trade min cannot exceed max'],
  [/^启用自动交易时，每小时笔数与成交量都必须大于 0$/, 'When enabled, hourly trades and volume must be > 0'],
  [/^交易已上链但执行失败\(revert\): (.+)$/, 'Transaction mined but reverted: $1'],
  [/^补 gas 交易已上链但执行失败\(revert\): (.+)$/, 'Gas top-up mined but reverted: $1'],
  [/^转账已上链但执行失败\(revert\): (.+)$/, 'Transfer mined but reverted: $1'],
  [/（含自动补 gas 的 (\S+) USDT）/, ' (incl. $1 USDT for auto gas top-up)'],
  [/买入/g, 'Buy'], [/卖出/g, 'Sell'], [/钱包 /g, 'Wallet ']
];

export function translateServer(text, lang) {
  if (lang !== 'en' || text == null) return text;
  let s = String(text);
  if (EN[s]) return EN[s];
  for (const [re, rep] of SERVER_PATTERNS) {
    if (re.test(s)) s = s.replace(re, rep);
  }
  return s;
}

export const LangContext = createContext({ lang: 'zh', setLang: () => {} });

// 组件内使用：const { t, ts, lang, setLang } = useI18n()
export function useI18n() {
  const { lang, setLang } = useContext(LangContext);
  const t = useCallback(
    (text, vars) => {
      let s = lang === 'en' ? EN[text] ?? text : text;
      // 特殊 key（同一中文对应不同英文时用 _后缀 区分），中文模式下去掉后缀标记
      if (lang !== 'en' && text.includes('_')) s = text.split('_')[0];
      if (lang === 'en' && s.startsWith('_')) s = s.slice(1);
      if (vars) for (const k of Object.keys(vars)) s = s.replaceAll(`{${k}}`, String(vars[k]));
      return s;
    },
    [lang]
  );
  const ts = useCallback((text) => translateServer(text, lang), [lang]);
  return { lang, setLang, t, ts };
}
